import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Sparkles, Check, X, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Qude Inference Engine confirm card (0.5-0.85 confidence band).
 *
 * Reached via the push notification the backend sends when a
 * viewing_session gets a mid-confidence LLM guess — the model reconstructed
 * a probable title from ambient play-pattern evidence alone, TMDB-validated
 * it, but isn't confident enough to auto-write. A tap here is the same
 * "no title = no write" gate every other detection source in Qude honors:
 * nothing lands in the hub without it.
 */
export default function InferenceConfirm() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(false);
  const [done, setDone] = useState(null); // 'confirmed' | 'dismissed'
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${API}/inference/session/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSession(res.data);
      } catch (err) {
        console.error('Failed to load inference session:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [sessionId, getToken]);

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const token = await getToken();
        const res = await axios.get(`${API}/tmdb/search`, {
          params: { q: query },
          headers: { Authorization: `Bearer ${token}` },
        });
        setSearchResults(res.data.results || []);
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query, getToken]);

  const confirm = async (override) => {
    setResponding(true);
    try {
      const token = await getToken();
      await axios.post(`${API}/inference/session/${sessionId}/confirm`, override || {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDone('confirmed');
      setTimeout(() => navigate('/hub'), 1500);
    } catch (err) {
      console.error('Failed to confirm inference:', err);
    } finally {
      setResponding(false);
    }
  };

  const dismiss = async () => {
    setResponding(true);
    try {
      const token = await getToken();
      await axios.post(`${API}/inference/session/${sessionId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDone('dismissed');
      setTimeout(() => navigate('/hub'), 1200);
    } catch (err) {
      console.error('Failed to dismiss inference:', err);
    } finally {
      setResponding(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050508' }}>
        <div style={{ width: 24, height: 24, border: '2px solid #7c3aed', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (done) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050508', flexDirection: 'column', gap: 16, padding: 24 }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%',
          background: done === 'confirmed' ? 'linear-gradient(135deg, #22c55e, #4A9EFF)' : 'rgba(255,255,255,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {done === 'confirmed' ? <Check size={32} style={{ color: '#fff' }} /> : <X size={32} style={{ color: '#fff' }} />}
        </div>
        <p style={{ color: '#fff', fontSize: 18, fontWeight: 800, textAlign: 'center' }}>
          {done === 'confirmed' ? 'Added to your Hub!' : "Got it — thanks for the correction."}
        </p>
        <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>Taking you back...</p>
      </div>
    );
  }

  if (!session) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050508' }}>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>This guess is no longer available.</p>
      </div>
    );
  }

  const epLabel = session.inferred_season
    ? `S${session.inferred_season}E${session.inferred_episode_start}${
        session.inferred_episode_end && session.inferred_episode_end !== session.inferred_episode_start
          ? `-${session.inferred_episode_end}` : ''
      }`
    : null;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050508', padding: 24 }}>
      <div style={{ maxWidth: 420, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Sparkles size={28} style={{ color: '#7c3aed' }} />
          </div>
          <div style={{
            display: 'inline-block', fontSize: 11, fontWeight: 700, marginBottom: 10,
            color: '#7c3aed', background: 'rgba(124,58,237,0.15)',
            border: '1px solid rgba(124,58,237,0.3)', borderRadius: 20, padding: '3px 12px',
          }}>
            Inferred from viewing pattern
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 900, color: '#fff', margin: '0 0 6px', lineHeight: 1.3 }}>
            Were you watching {session.inferred_title}{epLabel ? ` ${epLabel}` : ''}?
          </h1>
          {session.inferred_reasoning && (
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', margin: 0 }}>{session.inferred_reasoning}</p>
          )}
        </div>

        <button
          onClick={() => confirm()}
          disabled={responding}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center',
            padding: '14px', borderRadius: 14, marginBottom: 10,
            background: 'linear-gradient(135deg, #22c55e, #4A9EFF)', border: 'none',
            color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer', opacity: responding ? 0.6 : 1,
          }}>
          <Check size={16} /> Yes, that's right
        </button>

        {/* Search field for a correction */}
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 12, color: 'rgba(255,255,255,0.3)' }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Not quite — search for the real title..."
            style={{
              width: '100%', boxSizing: 'border-box', padding: '10px 12px 10px 36px', borderRadius: 14,
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
              color: '#fff', fontSize: 14, outline: 'none',
            }}
          />
          {searching && <div style={{ position: 'absolute', right: 12, top: 13, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>...</div>}
        </div>

        {searchResults.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16, maxHeight: 240, overflowY: 'auto' }}>
            {searchResults.map((r) => (
              <button
                key={`${r.tmdb_id}-${r.title}`}
                onClick={() => confirm({ title: r.title, tmdb_id: String(r.tmdb_id), media_type: r.media_type })}
                disabled={responding}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
                  padding: '8px 12px', borderRadius: 12,
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                  color: '#fff', cursor: 'pointer',
                }}>
                {r.poster_url ? (
                  <img src={r.poster_url} alt="" style={{ width: 30, height: 44, borderRadius: 5, objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 30, height: 44, borderRadius: 5, background: '#1a1a1a', flexShrink: 0 }} />
                )}
                <div style={{ fontSize: 13, fontWeight: 600 }}>{r.title} {r.year ? <span style={{ color: 'rgba(255,255,255,0.35)' }}>({r.year})</span> : null}</div>
              </button>
            ))}
          </div>
        )}

        <button
          onClick={dismiss}
          disabled={responding}
          style={{
            width: '100%', padding: '12px', borderRadius: 14,
            background: 'transparent', border: '1px solid rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.5)', fontSize: 14, fontWeight: 700,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
          <X size={14} /> Not watching anything
        </button>
      </div>
    </div>
  );
}
